'use client';

import { _ as _extends, a as _async_to_generator } from './cc-CgYInQop.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { sendGTMEvent } from '@next/third-parties/google';
import * as Checkbox from '@radix-ui/react-checkbox';
import { useTimeoutEffect } from '@react-hookz/web';
import { getCookie, hasCookie, setCookie } from 'cookies-next/client';
import { useRef, useState, useEffect } from 'react';
import { HiCheck } from 'react-icons/hi2';
import Form from 'next/form';
import { useAnalytics } from 'use-analytics';
import { Button } from '../atoms/shadcn/button.js';
import { COOKIE_CONSENT } from '@repo/constants';
import { useGetIntl } from '@repo/i18n/intl/client';
import { defaultConsentSettings, consentTypes, cn, cookieOptions, denyAllAdditionalConsentSettings, acceptAllConsentSettings } from '@repo/lib';
function ConsentManagerClient({
  className,
  dictionaryPromise,
  lang
}) {
  var _popoverRef_current;
  const intl = useGetIntl({
    dictionaryPromise,
    lang
  });
  const id = "consent-manager";
  const popoverRef = useRef(null);
  const formElement = (_popoverRef_current = popoverRef.current) == null ? void 0 : _popoverRef_current.querySelector("form");
  const {
    track
  } = useAnalytics() || {};
  // 1. Initialize state with a non-client-dependent default.
  // This ensures the server and initial client render are identical.
  const [consentSettings, setConsentSettings] = useState(defaultConsentSettings);
  const [hasMounted, setHasMounted] = useState(false);
  // 2. Use useEffect to update the state on the client after mounting.
  useEffect(() => {
    setHasMounted(true);
  }, []);
  useEffect(() => {
    // This block only runs on the client, after the initial render.
    if (hasMounted) {
      const consentCookieData = getCookie(COOKIE_CONSENT);
      if (consentCookieData) {
        const userConsentSettings = JSON.parse(consentCookieData);
        const mergedConsentSettings = _extends({}, defaultConsentSettings, userConsentSettings);
        setConsentSettings(mergedConsentSettings);
      }
    }
  }, [hasMounted]);
  const acceptSelectedConsents = () => _async_to_generator(function* () {
    var _popoverRef_current_0;
    const formData = new FormData(formElement);
    const userConsentSettings_0 = Object.fromEntries(formData.entries());
    const mergedConsentSettings_0 = _extends({}, defaultConsentSettings, userConsentSettings_0);
    setCookie(COOKIE_CONSENT, mergedConsentSettings_0, cookieOptions);
    setConsentSettings(mergedConsentSettings_0);
    sendGTMEvent({
      event: "consent",
      value: "default",
      gtm: mergedConsentSettings_0
    });
    if (track) {
      track("consent_accept_selected", mergedConsentSettings_0);
    }
    (_popoverRef_current_0 = popoverRef.current) == null ? void 0 : _popoverRef_current_0.hidePopover();
  })();
  const acceptAllConsents = () => _async_to_generator(function* () {
    var _popoverRef_current_1;
    setCookie(COOKIE_CONSENT, acceptAllConsentSettings, cookieOptions);
    setConsentSettings(acceptAllConsentSettings);
    sendGTMEvent({
      event: "consent",
      value: "default",
      gtm: acceptAllConsentSettings
    });
    if (track) {
      track("consent_accept_all", acceptAllConsentSettings);
    }
    (_popoverRef_current_1 = popoverRef.current) == null ? void 0 : _popoverRef_current_1.hidePopover();
  })();
  const denyAllAdditionalConsents = () => _async_to_generator(function* () {
    var _popoverRef_current_2;
    setCookie(COOKIE_CONSENT, denyAllAdditionalConsentSettings, cookieOptions);
    setConsentSettings(denyAllAdditionalConsentSettings);
    sendGTMEvent({
      event: "consent",
      value: "default",
      gtm: denyAllAdditionalConsentSettings
    });
    if (track) {
      track("consent_deny_all", denyAllAdditionalConsentSettings);
    }
    (_popoverRef_current_2 = popoverRef.current) == null ? void 0 : _popoverRef_current_2.hidePopover();
  })();
  const manageConsents = () => _async_to_generator(function* () {
    if (track) {
      track("consent_manage", denyAllAdditionalConsentSettings);
    }
  })();
  useTimeoutEffect(() => {
    var _popoverRef_current_3;
    (_popoverRef_current_3 = popoverRef.current) == null ? void 0 : _popoverRef_current_3.showPopover();
  }, hasCookie(COOKIE_CONSENT) ? undefined : 10000);
  return /*#__PURE__*/jsx("section", {
    className: cn("fixed inset-0 p-4 w-full max-w-2xl", "transition-discrete transition-all duration-300 ease-in-out", "opacity-0 open:opacity-100 open:translate-x-0 starting:open:opacity-0", "backdrop:bg-background/25 backdrop:backdrop-blur-md", "overflow-y-auto max-h-screen", className),
    id: id,
    popover: "auto",
    ref: popoverRef,
    children: /*#__PURE__*/jsxs(Form, {
      action: acceptSelectedConsents,
      className: "grid gap-4 place-content-center",
      children: [/*#__PURE__*/jsx("h2", {
        className: "text-lg uppercase",
        children: intl.formatMessage({
          defaultMessage: "Consent settings",
          description: "Text for the title of a consent settings dialog in the ConsentDialog component",
          id: "5xAMU8"
        })
      }), /*#__PURE__*/jsx("span", {
        className: "text-sm",
        children: intl.formatMessage({
          defaultMessage: "Choose your consent to view content available to you",
          description: "Text for the description of a consent settings dialog in the ConsentDialog component",
          id: "Oj8Ii2"
        })
      }), /*#__PURE__*/jsxs("div", {
        className: "grid gap-4 grid-cols-2",
        children: [/*#__PURE__*/jsxs("details", {
          className: "flex flex-col-reverse gap-4 col-span-2",
          children: [/*#__PURE__*/jsxs("menu", {
            className: "grid gap-4",
            children: [consentTypes.map(consentType => /*#__PURE__*/jsxs("fieldset", {
              className: cn("grid grid-cols-[auto_1fr] items-center gap-4 text-xs sm:text-sm md:text-base"),
              children: [/*#__PURE__*/jsx(Checkbox.Root, {
                className: "bg-background-inverted text-foreground-inverted size-5 rounded-xs border border-solid",
                // On the initial render, this is based on default settings.
                // It updates to the user's settings after the client-side effect runs.
                checked: consentSettings[consentType.name] === "granted",
                id: consentType.name,
                name: consentType.name,
                value: "granted",
                onCheckedChange: isChecked => {
                  setConsentSettings(_extends({}, consentSettings, {
                    [consentType.name]: isChecked ? "granted" : "denied"
                  }));
                },
                children: /*#__PURE__*/jsx(Checkbox.Indicator, {
                  suppressHydrationWarning: true,
                  children: /*#__PURE__*/jsx(HiCheck, {})
                })
              }), /*#__PURE__*/jsx("label", {
                htmlFor: consentType.name,
                children: consentType.description
              })]
            }, consentType.name)), /*#__PURE__*/jsx(Button, {
              "aria-label": intl.formatMessage({
                defaultMessage: "Accept selected",
                description: "Text for a button to accept selected consent settings",
                id: "B/EzHv"
              }),
              onClick: acceptSelectedConsents,
              popoverTarget: id,
              popoverTargetAction: "hide",
              size: "sm",
              variant: "default",
              children: intl.formatMessage({
                defaultMessage: "Accept selected",
                description: "Text for a button to accept selected consent settings",
                id: "B/EzHv"
              })
            })]
          }), /*#__PURE__*/jsx("summary", {
            className: "cursor-pointer",
            onClick: manageConsents,
            children: intl.formatMessage({
              defaultMessage: "Manage",
              description: "Text for a button to manage consent settings",
              id: "RZB/it"
            })
          })]
        }), /*#__PURE__*/jsx(Button, {
          "aria-label": intl.formatMessage({
            defaultMessage: "Accept all",
            description: "Text for a button to accept all consent settings",
            id: "IpWqxd"
          }),
          onClick: acceptAllConsents,
          popoverTarget: id,
          popoverTargetAction: "hide",
          type: "button",
          size: "sm",
          variant: "default",
          children: intl.formatMessage({
            defaultMessage: "Accept all",
            description: "Text for a button to accept all consent settings",
            id: "IpWqxd"
          })
        }), /*#__PURE__*/jsx(Button, {
          "aria-label": intl.formatMessage({
            defaultMessage: "Deny all",
            description: "Text for a button to deny all consent settings",
            id: "hWlrfd"
          }),
          onClick: denyAllAdditionalConsents,
          popoverTarget: id,
          popoverTargetAction: "hide",
          type: "button",
          size: "sm",
          variant: "secondary",
          children: intl.formatMessage({
            defaultMessage: "Deny all",
            description: "Text for a button to deny all consent settings",
            id: "hWlrfd"
          })
        })]
      })]
    })
  });
}
export { ConsentManagerClient as C };